import type { Procedure, ProcessContract } from '@bk/process-dsl';
import { humanize } from '../../lib/format';
import { Badge } from '../../ui';

const TYPE_LABEL: Record<string, string> = { lista: 'elenco', testo: 'testo', numero: 'numero', data: 'data', documento: 'documento', presenze: 'presenze' };

/**
 * Il contratto in forma leggibile: è ciò che vede l'azienda. Il YAML è il formato interno
 * (scritto dal motore dalla Fase 3) e resta nella sezione "avanzato".
 */
export function ContractView({ contract, procedure }: { contract: ProcessContract; procedure?: Procedure }) {
  const steps = procedure?.steps;
  return (
    <div className="space-y-5 text-sm">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink/50">Obiettivo</h3>
        <p className="mt-1">{contract.goal}</p>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink/50">Informazioni necessarie</h3>
        {contract.information.length === 0 && <p className="mt-1 text-ink/60">Nessuna.</p>}
        <ul className="mt-1 space-y-1">
          {contract.information.map((i) => (
            <li key={i.key}>
              <span className="font-medium">{i.description ?? humanize(i.key)}</span> <span className="text-ink/50">({TYPE_LABEL[i.type]})</span>
              <span className="block text-xs text-ink/55">
                a mano: {humanize(i.base)}
                {i.plus.length > 0 && <> · in futuro: {i.plus.map(humanize).join(', ')}</>}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink/50">Ruoli e assegnazione</h3>
        <p className="mt-1">
          {contract.roles.map(humanize).join(', ')} · assegnati per {contract.assignment.join(', ')}
        </p>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink/50">Passi</h3>
        <ol className="mt-1 space-y-2">
          {(steps ?? contract.actions.map((a) => ({ action: a.key, title: a.description ?? humanize(a.key), executor: a.executor, startsWhen: a.when ? `quando ${a.when.action} è chiusa` : "all'avvio", outcomes: a.outcomes, reopenOn: a.reopenOn, confirmation: a.confirmation, riskClass: a.riskClass, tools: [] as string[], message: a.message }))).map((s, i) => (
            <li key={s.action} className="rounded-lg border border-black/10 px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-ink/40">{i + 1}.</span>
                <span className="font-medium">{s.title.replace(/\{[a-z_]+\}/g, '…')}</span>
                <span className="text-ink/55">· {humanize(s.executor)}</span>
                {s.confirmation === 'obbligatoria' && <Badge tone="red">conferma obbligatoria</Badge>}
              </div>
              <p className="text-xs text-ink/55">
                {s.startsWhen}
                {s.outcomes.length > 0 && <> · esiti: {s.outcomes.join(', ')}</>}
                {Object.entries(s.reopenOn).map(([o, a]) => ` · se ${o} riapre ${a}`)}
                {s.tools.length > 0 && <> · strumenti: {s.tools.join(', ')}</>}
              </p>
            </li>
          ))}
        </ol>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink/50">Si chiude quando</h3>
        <ul className="mt-1 list-inside list-disc">
          {contract.closure.map((c, i) => (
            <li key={i}>
              tutte le {humanize(c.action).toLowerCase()} sono chiuse{c.outcome ? ` con esito ${c.outcome}` : ''}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
